import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Home from './pages/Home';
import Features from './pages/Features';
import About from './pages/About';
import Services from './pages/Services';
import Contact from './pages/Contact';
import Resources from './pages/Resources';
import BlogDetail from './pages/BlogDetail';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import Register from './pages/Register';
import AcceptInvite from './pages/AcceptInvite';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
// User Dashboard & Account
import UserDashboard from './pages/UserDashboard';
import MyAssessments from './pages/MyAssessments';
import Account from './pages/Account';
import Billing from './pages/Billing';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PlanProvider } from './context/PlanContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { CommandProvider } from './context/CommandContext';
import { StripeProvider } from './context/StripeContext';
import { CreditGuardProvider } from './context/CreditGuardContext';
import { ApprovalWSProvider } from './context/ApprovalWSContext';
import { ModeProvider } from './context/ModeContext';
import ProtectedRoute from './components/admin/ProtectedRoute';
import AdminProtectedRoute from './components/admin/AdminProtectedRoute';
import UserProtectedRoute from './components/UserProtectedRoute';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import LandingHeader from './components/LandingHeader';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';
import AdminLogin from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/AdminUsers';
import AdminBilling from './pages/AdminBilling';
import AdminOrganizations from './pages/admin/AdminOrganizations';
import AdminOrgDetail from './pages/admin/AdminOrgDetail';
// PostHog Analytics
import { initPostHog, checkSessionRecording, trackPageview } from './lib/posthog';
import PostHogRouteTracker from './components/PostHogRouteTracker';
import AdminPlans from './pages/admin/AdminPlans';

// New Admin Components
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import OrganizationsList from './pages/admin/OrganizationsList';
import OrganizationDetail from './pages/admin/OrganizationDetail';
import UsersList from './pages/admin/UsersList';
import UserDetail from './pages/admin/UserDetail';
import BillingFinance from './pages/admin/BillingFinance';
import SiteAnalytics from './pages/admin/SiteAnalytics';
import AdminBackups from './pages/admin/AdminBackups';
import BlogsManagement from './pages/admin/BlogsManagement';
import BlogEditorNew from './pages/admin/BlogEditorNew';
import EbooksManagement from './pages/admin/EbooksManagement';
import EbookEditorNew from './pages/admin/EbookEditorNew';
import EbookDownloadsManagement from './pages/admin/EbookDownloadsManagement';
import MessagesManagement from './pages/admin/MessagesManagement';
import LegalManagement from './pages/admin/LegalManagement';
import AdminTemplateManager from './pages/AdminTemplateManager';
import AdminSubmissions from './pages/admin/AdminSubmissions';
import MySubmissions from './pages/MySubmissions';
import DeveloperPortal from './pages/DeveloperPortal';
import AdminReviewQueue from './pages/AdminReviewQueue';
import AppLibrary from './pages/AppLibrary';
import AppStore from './pages/AppStore';
import MyAppSubmissions from './pages/MyAppSubmissions';
import AdminAppReviewQueue from './pages/AdminAppReviewQueue';
import PlatformSettings from './pages/admin/PlatformSettings';
import LogsAudit from './pages/admin/LogsAudit';
import NotificationsPage from './pages/admin/NotificationsPage';
import AdminForgotPassword from './pages/admin/ForgotPassword';
import AdminResetPassword from './pages/admin/ResetPassword';
import AdminUsersManagement from './pages/admin/AdminUsersManagement';
import LLMUsageAdmin from './pages/admin/LLMUsageAdmin';
import AdminLLMKeys from './pages/admin/AdminLLMKeys';
import TrackingDiagnostics from './pages/admin/TrackingDiagnostics';
// Legal pages
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import AffiliateTerms from './pages/AffiliateTerms';
import CookiePolicy from './pages/CookiePolicy';
import AIDisclaimer from './pages/AIDisclaimer';
import CookieSettings from './pages/CookieSettings';
// Assessment pages
import AssessmentLanding from './pages/AssessmentLanding';
import ConsultantLanding from './pages/ConsultantLanding';
import BusinessLanding from './pages/BusinessLanding';
import AssessmentWizard from './pages/AssessmentWizard';
import AssessmentResults from './pages/AssessmentResults';
import AssessmentHistory from './pages/AssessmentHistory';
import ConsultantDashboard from './pages/ConsultantDashboard';
import ConsultantAssessmentDetail from './pages/ConsultantAssessmentDetail';
import ProcessLibrary from './pages/ProcessLibrary';
import CreateProcess from './pages/CreateProcess';
import ProcessDetail from './pages/ProcessDetail';
import JobDescriptions from './pages/JobDescriptions';
import TooltipTest from './pages/TooltipTest';
// Agent Builder (Phase 1 - Partner Pro)
import AIWorkforceDashboard from './pages/AIWorkforceDashboard';
import OrgChartBuilder from './pages/OrgChartBuilder';
import AgentList from './pages/AgentList';
import CreateAgentPage from './pages/CreateAgentPage';
import AgentDetail from './pages/AgentDetail';
import AgentBuilder from './pages/AgentBuilder';
import AgentEditor from './pages/AgentEditor';
import OAuthIntegrations from './pages/OAuthIntegrations';
import TeamManagement from './pages/TeamManagement';
import AuditLogs from './pages/AuditLogs';
import OrganizationSettings from './pages/OrganizationSettings';
import OrgAIConfiguration from './pages/OrgAIConfiguration';
import AgentAnalyticsDashboard from './pages/AgentAnalyticsDashboard';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import AgentAnalytics from './pages/AgentAnalytics';
import AnalyticsReports from './pages/AnalyticsReports';
import ProductAnalytics from './pages/ProductAnalytics';
import UnifiedAnalytics from './pages/UnifiedAnalytics';
import HelpCentre from './pages/HelpCentre';
import HelpCategory from './pages/HelpCategory';
import HelpArticle from './pages/HelpArticle';
import ProcessTemplates from './pages/ProcessTemplates';
import OpportunitiesDashboard from './pages/OpportunitiesDashboard';
import ReportsPage from './pages/ReportsPage';
import MyAutomations from './pages/MyAutomations';
import OpportunityDetail from './pages/OpportunityDetail';
import OrgChartPage from './pages/OrgChartPage';
import VoiceSettings from './pages/VoiceSettings';
import VoiceAnalyticsDashboard from './pages/VoiceAnalyticsDashboard';
import OrgAdminPanel from './pages/OrgAdminPanel';
import UnifiedAnalyticsDashboard from './pages/UnifiedAnalyticsDashboard';
import AgentTemplatesLibrary from './pages/AgentTemplatesLibrary';
import AgentMarketplaceV2 from './pages/AgentMarketplaceV2';
// CommandBarV2 now rendered inside AppShell, not App.js
import MyAgents from './pages/MyAgents';
import SessionsList from './pages/SessionsList';
import RunHistory from './pages/RunHistory';
import RunHealthDashboard from './pages/RunHealthDashboard';
import SessionDetail from './pages/SessionDetail';
import Inbox from './pages/Inbox';
import Schedules from './pages/workspace/Schedules';
import WorkflowsList from './pages/WorkflowsList';
import WorkflowDetail from './pages/WorkflowDetail';
import WorkflowRunDetail from './pages/WorkflowRunDetail';
import WorkflowsComingSoon from './pages/WorkflowsComingSoon';
import featureFlags from './config/featureFlags';
// Agent Dashboard Pages
import DashboardsList from './pages/workspace/DashboardsList';
import AgentDashboard from './pages/workspace/AgentDashboard';
import IntegrationsSettings from './pages/admin/IntegrationsSettings';
import TeamMembers from './pages/admin/TeamMembers';
// Approval Dashboard
import ApprovalDashboard from './pages/ApprovalDashboard';
import ExternalAgentLibrary from './pages/ExternalAgentLibrary';
import MyExternalAgents from './pages/MyExternalAgents';
import ExternalAgentReview from './pages/ExternalAgentReview';
import ExternalAgentHealth from './pages/ExternalAgentHealth';
// MCP Settings
import MCPSettings from './pages/MCPSettings';
// Learn/Tutorial Pages
import WebhookTutorialPage from './pages/learn/WebhookTutorialPage';
// Client Organizations Management (Phase 3.3)
import ClientOrganizationsPage from './pages/settings/ClientOrganizationsPage';
// Partner Program
import PartnerApply from './pages/PartnerApply';
import PartnerDirectory from './pages/PartnerDirectory';
import AdminPartners from './pages/admin/AdminPartners';
import AdminAffiliates from './pages/admin/AdminAffiliates';
import AdminDocumentation from './pages/admin/AdminDocumentation';
import PartnerProgram from './pages/PartnerProgram';
// New Footer Pages
import Security from './pages/Security';
import UseCases from './pages/UseCases';
import Roadmap from './pages/Roadmap';
import HowWeScore from './pages/HowWeScore';
import Releases from './pages/Releases';
import Careers from './pages/Careers';
import Status from './pages/Status';
import Integrations from './pages/Integrations';
// SEO Concept Pages (Public - SERP Sitelinks)
import AIAgentsPage from './pages/AIAgentsPage';
import CommandPage from './pages/CommandPage';
import CreateAgentConceptPage from './pages/CreateAgentPage';
import SecurityPage from './pages/SecurityPage';
import MCPPage from './pages/MCP';
// Orion Landing Page (Paid Acquisition)
import OrionLanding from './pages/OrionLanding';
// Assignment Marker Landing Page (Paid Acquisition - Teachers)
import AssignmentMarkerLanding from './pages/AssignmentMarkerLanding';
import OAuthCallback from './pages/OAuthCallback';
// Public Automations Gallery (No Auth Required)
import AutomationsGallery from './pages/AutomationsGallery';
import AutomationDetail from './pages/AutomationDetail';
import WorkerDetail from './pages/WorkerDetail';
import MemoryInspector from './pages/MemoryInspector';
import MappingDashboard from './pages/MappingDashboard';
import SystemRuns from './pages/SystemRuns';
import SystemRunDetail from './pages/SystemRunDetail';
import SystemsPage from './pages/SystemsPage';
import SystemDetailPage from './pages/SystemDetailPage';
import PublicAgentPage from './pages/PublicAgentPage';
import InstallRedirect from './pages/InstallRedirect';
import CreatorProfile from './pages/CreatorProfile';
import { PortalLanding, PortalVerify, PortalDashboard } from './pages/CustomerPortal';
// Blueprint Preview (Public - No Auth Required)
import BlueprintPreview from './pages/BlueprintPreview';
// Blueprint Continue (Post-Signup Claim Flow)
import BlueprintContinue from './pages/BlueprintContinue';
// Automation Layer V1
import AutomationIntegrations from './pages/AutomationIntegrations';
import Automations from './pages/Automations';
// Single Login + RBAC Landing Pages
import PartnerProDashboard from './pages/PartnerProDashboard';
import WorkspaceHome from './pages/workspace/WorkspaceHome';
import WorkspaceLite from './pages/WorkspaceLite';
import UnifiedDashboard from './pages/Dashboard';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import PublicBetaFooter from './components/PublicBetaFooter';
import Feedback from './pages/Feedback';
import GlobalAuthGate from './components/GlobalAuthGate';
import AuthRouteGuard from './components/AuthRouteGuard';
// Agent Runtime (Phase 2 - Partner Pro)
import AgentTestConsole from './pages/AgentTestConsole';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import ImpersonationBanner from './components/ImpersonationBanner.jsx';
// Universal Voice Wizard (Phase 4)
import UniversalVoiceWizard from './components/UniversalVoiceWizard';
// Voice Demo Wizard (Basic plan sandbox)
import VoiceDemoWizard from './pages/VoiceDemoWizard';
import BusinessSetupWizard from './pages/BusinessSetupWizard';
// Route Guards (tier gating)
import { VoiceSetupGuard, AdvancedRouteGuard, FeatureGuard } from './components/RouteGuards';
// Meeting Integration Wizard (for Meeting Notes Agent Connected)
import MeetingIntegrationWizard from './components/MeetingIntegrationWizard';
// Capability-first Agent Creation Wizard (from Opportunity)
import CreateAgentFromOpportunityWizard from './components/CreateAgentFromOpportunityWizard';
// Unified Agent Builder (Phase 1-2)
import UnifiedAgentBuilder from './pages/UnifiedAgentBuilder';
// Execution Agents (Dark Deploy - Beta Access Only)
// Note: Operators.jsx still exports CreateOperatorModal used by OperatorDetail.js
// The legacy /internal/execution-agents route now redirects to /agents
import OperatorDetail from './pages/OperatorDetail';
// Command (ACC) - Global Command Drawer
// Command split pane is now in AppShell — no separate import needed
import { useCommand } from './context/CommandContext';
import RunAgentModal from './components/RunAgentModal';
// Service OS — Bounded domain for service businesses
import SOSLayout from './service-os/components/SOSLayout';
import SOSGate from './service-os/components/SOSGate';
import SOSOnboarding from './service-os/pages/SOSOnboarding';
import SOSDashboard from './service-os/pages/SOSDashboard';
import SOSLeads from './service-os/pages/SOSLeads';
import SOSLeadDetail from './service-os/pages/SOSLeadDetail';
import SOSJobs from './service-os/pages/SOSJobs';
import SOSSettings from './service-os/pages/SOSSettings';
import PublicEnquiryPage from './service-os/pages/PublicEnquiryPage';
// New Service UI Pages
import ServiceHome from './pages/ServiceHome';
import ServiceInbox from './pages/ServiceInbox';
import ServiceAutomations from './pages/ServiceAutomations';
import ServiceSettings from './pages/ServiceSettings';
import AutomationBuilder from './pages/AutomationBuilder';
import AppsPage from './pages/AppsPage';
import ServiceMarketplace from './pages/ServiceMarketplace';
import WorkerSetupFlow from './pages/WorkerSetupFlow';

// GlobalCommandDrawer REMOVED — Command is now a split pane in AppShell

// Legacy Route Redirects - Redirect old agent creation routes to Unified Agent Builder
// Per consolidation spec: No separate advanced builder page should remain accessible
const LegacyCreateAgentRedirect = () => {
  // Redirect /partner-pro/ai-workforce/agents/create → /unified-agent-builder (shows entry selector)
  return <Navigate to="/unified-agent-builder" replace />;
};

const LegacyCreateFromOpportunityRedirect = () => {
  const { opportunityId } = useParams();
  // Redirect to unified builder with opportunity context
  return <Navigate to={`/unified-agent-builder?from=opportunity&sourceId=${opportunityId}`} replace />;
};

// Phase 1 Consolidation: Agent Detail → Unified Builder
// Clicking any existing agent should open the Unified Agent Builder
const LegacyAgentDetailRedirect = () => {
  const { agentId } = useParams();
  return <Navigate to={`/unified-agent-builder/${agentId}`} replace />;
};

// Phase 1 Consolidation: Agent Editor → Unified Builder
const LegacyAgentEditorRedirect = () => {
  const { agentId } = useParams();
  return <Navigate to={`/unified-agent-builder/${agentId}`} replace />;
};

// Phase 1 Consolidation: Test Console → Unified Builder
// Test Console functionality is now in the Live Preview panel of Unified Builder
// Global Error Handlers - Captures beforeunload, unhandledrejection, error events
import GlobalErrorHandlers from './components/GlobalErrorHandlers';
// Completion Guard - Prevents navigation to wizard when completion is in progress
import CompletionGuard from './components/CompletionGuard';

import {
  IconContext,
  Lightning
} from '@phosphor-icons/react';
// Global RunAgentModal - Opens when Command requests to run an agent
// BLOCKS Live Voice S2S agents - they run via telephony only
const GlobalRunAgentModal = () => {
  const { agentToRun, clearAgentToRun } = useCommand();
  
  if (!agentToRun) return null;
  
  // Block Live Voice S2S agents - they cannot be run from Command/UI
  const isLiveVoiceS2S = agentToRun.capability_profile === 'live_voice_s2s_agent' || 
                         agentToRun.run_modal_variant === 'voice_s2s_informational';
  
  if (isLiveVoiceS2S) {
    // Clear without opening modal - the block message was already shown in Command
    clearAgentToRun();
    return null;
  }
  
  return (
    <RunAgentModal
      agent={agentToRun}
      isOpen={!!agentToRun}
      onClose={clearAgentToRun}
    />
  );
};

// GlobalCommandLauncher FAB REMOVED — Command is now a split pane in AppShell

// Create a context to pass the auth remount function down
const AuthRemountContext = React.createContext(null);

export const useAuthRemount = () => {
  const context = React.useContext(AuthRemountContext);
  if (!context) {
    console.warn('[AUTH REMOUNT] useAuthRemount called outside provider - returning noop');
    return () => {};
  }
  return context;
};

// Redirect component that preserves route parameters
const AgentAnalyticsRedirect = () => {
  const { agentId } = useParams();
  console.log('[AgentAnalyticsRedirect] Redirecting agentId:', agentId);
  return <Navigate to={`/analytics/agents/${agentId}`} replace />;
};

function App() {
  // CRITICAL FIX: Key to force AuthProvider remount on login/logout
  // This destroys stale admin state when member logs in
  const [authInstanceKey, setAuthInstanceKey] = React.useState(0);
  
  const forceAuthRemount = React.useCallback(() => {
    // ═══════════════════════════════════════════════════════════════════════════════
    // COMPLETION GUARD: Don't remount auth during assessment completion flow
    // This prevents the reload bug where modal appears briefly then page reloads
    // ═══════════════════════════════════════════════════════════════════════════════
    if (window.__MM_COMPLETION_IN_PROGRESS) {
      console.log('[AUTH REMOUNT] BLOCKED - completion in progress');
      return;
    }
    
    // Also check sessionStorage flag
    try {
      const completionFlag = sessionStorage.getItem('mm_assessment_completion_in_progress');
      if (completionFlag) {
        console.log('[AUTH REMOUNT] BLOCKED - completion flag in sessionStorage');
        return;
      }
    } catch (e) {
      // Ignore sessionStorage errors
    }
    
    // Also check URL for success parameter
    if (window.location.search.includes('success=1')) {
      console.log('[AUTH REMOUNT] BLOCKED - success=1 in URL');
      return;
    }
    
    console.log('[AUTH REMOUNT] Forcing AuthProvider remount, key:', authInstanceKey, '→', authInstanceKey + 1);
    console.trace('[AUTH REMOUNT_CALLER] Layers trace:');
    setAuthInstanceKey(prev => prev + 1);
  }, [authInstanceKey]);

  // Initialize PostHog analytics on app startup
  useEffect(() => {
    initPostHog();
  });

  return (
    <IconContext.Provider value={{ weight: 'duotone' }}>
    <HelmetProvider>
      <AuthRemountContext.Provider value={forceAuthRemount}>
        <AuthProvider key={authInstanceKey}>
        {/* GLOBAL AUTH GATE: Single place that shows AuthLoadingScreen during auth check */}
        <GlobalAuthGate>
        <PlanProvider>
          <ModeProvider>
          <OrganizationProvider>
            <StripeProvider>
            <CreditGuardProvider>
            <CommandProvider>
            <ApprovalWSProvider>
            <div className="App noise-texture">
              <BrowserRouter>
              {/* PostHog Route Tracker - manages session recording */}
              <PostHogRouteTracker />
              {/* Global Error Handlers - Captures beforeunload, unhandledrejection, error events */}
              <GlobalErrorHandlers />
              {/* Global Completion Guard - Redirects wizard to results when completion lock exists */}
              <CompletionGuard>
              {/* Command split pane is now inside AppShell — no separate rendering needed */}
              {/* Global RunAgentModal */}
              <GlobalRunAgentModal />
              <ImpersonationBanner />
              <ScrollToTop />
              <LandingHeader />
              <Toaster />
              <SonnerToaster />
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/features" element={<Features />} />
              <Route path="/about" element={<About />} />
              {/* Redirect legacy /services to /features */}
              <Route path="/services" element={<Navigate to="/features" replace />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/blog/:blogId" element={<BlogDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/pricing" element={<Pricing />} />
              
              {/* Partner Program - Public */}
              <Route path="/partners" element={<PartnerDirectory />} />
              <Route path="/partners/apply" element={<PartnerApply />} />
              <Route path="/partners/program" element={<PartnerProgram />} />
              
              {/* Consultant/Agency Landing Page */}
              <Route path="/consultants" element={<ConsultantLanding />} />
              <Route path="/agencies" element={<ConsultantLanding />} />
              
              {/* Business Landing Page */}
              <Route path="/businesses" element={<BusinessLanding />} />
              
              {/* Orion Landing Page - Paid Acquisition */}
              <Route path="/orion" element={<OrionLanding />} />
              
              {/* Assignment Marker Landing Page - Paid Acquisition (Teachers) */}
              <Route path="/assignment-marker" element={<AssignmentMarkerLanding />} />

              {/* OAuth callback — lightweight page for Composio popup */}
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              
              {/* Public Worker Marketplace (No Auth Required) */}
              <Route path="/marketplace" element={<AutomationsGallery />} />
              <Route path="/marketplace/:slug" element={<AutomationDetail />} />
              {/* Legacy /automations public redirects */}
              <Route path="/automations" element={<Navigate to="/marketplace" replace />} />
              <Route path="/automations/:slug" element={<AutomationDetail />} />
              <Route path="/public/agent/:publicKey" element={<PublicAgentPage />} />
              <Route path="/install/:slug" element={<InstallRedirect />} />
              <Route path="/creators/:username" element={<CreatorProfile />} />

              {/* Customer Portal — public, branded, end-client facing */}
              <Route path="/portal/:slug" element={<PortalLanding />} />
              <Route path="/portal/:slug/verify" element={<PortalVerify />} />
              <Route path="/portal/:slug/dashboard" element={<PortalDashboard />} />
              
              {/* Blueprint Preview - Public (No Auth Required) */}
              <Route path="/blueprint-preview/:draftToken" element={<BlueprintPreview />} />
              
              {/* Blueprint Continue - Post-Signup Claim Flow (requires auth but handles its own redirect) */}
              <Route path="/blueprint/continue" element={
                <UserProtectedRoute>
                  <BlueprintContinue />
                </UserProtectedRoute>
              } />
              
              {/* SEO Concept Pages - Public (SERP Sitelinks) */}
              <Route path="/ai-agents" element={<AIAgentsPage />} />
              <Route path="/command" element={<CommandPage />} />
              <Route path="/create-agent" element={<CreateAgentConceptPage />} />
              <Route path="/mcp" element={<MCPPage />} />
              
              {/* Tutorial Pages - Public */}
              <Route path="/learn/tutorials/webhook-agent" element={<WebhookTutorialPage />} />
              
              {/* Footer Pages - Public */}
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/use-cases" element={<UseCases />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/how-we-score" element={<HowWeScore />} />
              <Route path="/releases" element={<Releases />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/status" element={<Status />} />
              
              {/* Public Marketplace Redirect - redirects to login then marketplace */}
              <Route path="/marketplace" element={<Navigate to="/login?redirect=/partner-pro/ai-workforce/marketplace-v2" replace />} />
              
              {/* Auth Routes - Wrapped with AuthRouteGuard to prevent rendering when authenticated */}
              <Route path="/login" element={<AuthRouteGuard><Login /></AuthRouteGuard>} />
              <Route path="/register" element={<AuthRouteGuard><Register /></AuthRouteGuard>} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* User Dashboard & Account */}
              {/* /dashboard now handled inside AuthenticatedLayout as UnifiedDashboard */}
              <Route path="/my-assessments" element={<UserProtectedRoute><MyAssessments /></UserProtectedRoute>} />
              <Route path="/my-scans" element={<UserProtectedRoute><MyAssessments /></UserProtectedRoute>} /> {/* Backward compatibility */}
              <Route path="/account" element={<UserProtectedRoute><Account /></UserProtectedRoute>} />
              <Route path="/billing" element={<UserProtectedRoute><Billing /></UserProtectedRoute>} />
              
              {/* Legal Pages */}
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/affiliate-terms" element={<AffiliateTerms />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/ai-disclaimer" element={<AIDisclaimer />} />
              <Route path="/cookie-settings" element={<CookieSettings />} />
              
              {/* Help Centre Routes - Public */}
              <Route path="/help" element={<HelpCentre />} />
              <Route path="/help/categories/:category" element={<HelpCategory />} />
              <Route path="/help/articles/:article_slug" element={<HelpArticle />} />

              {/* Backward compatibility routes - redirect to authenticated assessments */}
              <Route path="/scanner" element={<Navigate to="/assessments" replace />} />
              
              {/* Consultant Dashboard - Outside main layout (different UX) */}
              <Route path="/consultant/dashboard" element={<ConsultantDashboard />} />
              <Route path="/consultant/assessments" element={<ConsultantDashboard />} />
              <Route path="/consultant/assessments/:assessmentId" element={<ConsultantAssessmentDetail />} />
              {/* Backward compatibility */}
              <Route path="/consultant/scans" element={<ConsultantDashboard />} />
              <Route path="/consultant/scans/:scanId" element={<ConsultantAssessmentDetail />} />
              
              {/* UNIFIED AUTHENTICATED LAYOUT - Single AppShell for Workspace + AI Workforce + Analytics */}
              <Route element={<UserProtectedRoute><AuthenticatedLayout /></UserProtectedRoute>}>
                {/* Assessment Routes - Inside layout for smooth navigation */}
                <Route path="/assessments" element={<MyAssessments />} />
                <Route path="/assessments/wizard" element={<AssessmentWizard />} />
                <Route path="/assessments/results/:assessmentId" element={<AssessmentResults />} />
                <Route path="/assessments/history" element={<AssessmentHistory />} />
                {/* Backward compatibility for scanner routes */}
                <Route path="/scanner/wizard" element={<AssessmentWizard />} />
                <Route path="/scanner/results/:scanId" element={<AssessmentResults />} />
                
                {/* Workspace Routes */}
                <Route path="/processes" element={<ProcessLibrary />} />
                <Route path="/processes/new" element={<CreateProcess />} />
                <Route path="/processes/:id" element={<ProcessDetail />} />
                <Route path="/processes/:id/edit" element={<CreateProcess />} />
                <Route path="/process-templates" element={<ProcessTemplates />} />
                <Route path="/job-descriptions" element={<JobDescriptions />} />
                <Route path="/opportunities" element={<OpportunitiesDashboard />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/my-automations" element={<MyAutomations />} />
                <Route path="/opportunities/:opportunityId" element={<OpportunityDetail />} />
                <Route path="/org-chart" element={<OrgChartPage />} />
                {/* Redirect old Org Admin to consolidated Organisation page */}
                <Route path="/settings/admin" element={<Navigate to="/settings" replace />} />
                
                {/* AI Workforce Routes - Admin Only */}
                <Route path="/partner-pro/ai-workforce" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <AIWorkforceDashboard />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/agents" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'member']}>
                    <AgentList />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/agents/create" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'member']}>
                    <LegacyCreateAgentRedirect />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/agents/create/edit" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'member']}>
                    <LegacyCreateAgentRedirect />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/agents/:agentId/edit" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'member']}>
                    <LegacyAgentEditorRedirect />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/agents/create-from-opportunity/:opportunityId" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'member']}>
                    <LegacyCreateFromOpportunityRedirect />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/agents/:agentId/wizard" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'editor', 'member']}>
                    <VoiceSetupGuard>
                      <UniversalVoiceWizard />
                    </VoiceSetupGuard>
                  </RoleProtectedRoute>
                } />
                {/* Voice Demo Wizard (Basic plan sandbox) */}
                <Route path="/voice/demo" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'editor', 'member']}>
                    <VoiceDemoWizard />
                  </RoleProtectedRoute>
                } />
                <Route path="/business-setup" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'editor', 'member']}>
                    <BusinessSetupWizard />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/agents/:agentId/meeting-wizard" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'editor', 'member']}>
                    <MeetingIntegrationWizard />
                  </RoleProtectedRoute>
                } />
                {/* Agent Details page - main view for agents */}
                <Route path="/partner-pro/ai-workforce/agents/:agentId/details" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'member']}>
                    <AgentDetail />
                  </RoleProtectedRoute>
                } />
                {/* Default agent route - shows Agent Details (use Edit button to go to builder) */}
                <Route path="/partner-pro/ai-workforce/agents/:agentId" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'member']}>
                    <AgentDetail />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/run-history" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <RunHistory />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/marketplace-v2" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <AgentMarketplaceV2 />
                  </RoleProtectedRoute>
                } />
                <Route path="/my-submissions" element={<MySubmissions />} />
                <Route path="/developer" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <DeveloperPortal />
                  </RoleProtectedRoute>
                } />
                <Route path="/app-library" element={<AppStore />} />
                <Route path="/app-store" element={<AppStore />} />
                <Route path="/my-app-submissions" element={<MyAppSubmissions />} />
                <Route path="/submit-app" element={<MyAppSubmissions />} />
                <Route path="/admin-review-queue" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <AdminReviewQueue />
                  </RoleProtectedRoute>
                } />
                <Route path="/admin-app-review" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <AdminAppReviewQueue />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/templates" element={<Navigate to="/partner-pro/ai-workforce/marketplace-v2" replace />} />
                {/* Agent Test Console - For testing non-voice agents */}
                <Route path="/partner-pro/ai-workforce/console" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <AgentTestConsole />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/voice-settings" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <VoiceSettings />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/org-chart" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <OrgChartBuilder />
                  </RoleProtectedRoute>
                } />
                
                {/* Unified Agent Builder - New hybrid chat/visual builder */}
                {/* Basic tier users can access with 3 draft agent limit */}
                <Route path="/unified-agent-builder" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'member']}>
                    <UnifiedAgentBuilder />
                  </RoleProtectedRoute>
                } />
                <Route path="/unified-agent-builder/:agentId" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin', 'member']}>
                    <UnifiedAgentBuilder />
                  </RoleProtectedRoute>
                } />
                
                {/* AI Workforce Sessions - Admin view of agent sessions */}
                <Route path="/partner-pro/ai-workforce/sessions" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <SessionsList />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/sessions/:session_id" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <SessionDetail />
                  </RoleProtectedRoute>
                } />
                
                {/* Single Login + RBAC Landing Pages */}
                
                {/* Unified Dashboard — All tiers — NEW SERVICE HOME */}
                <Route path="/dashboard" element={<ServiceHome />} />
                
                {/* New Service Pages */}
                <Route path="/grow" element={<ServiceMarketplace />} />
                <Route path="/inbox" element={<ServiceInbox />} />
                <Route path="/apps" element={<AppsPage />} />
                
                {/* Workers - Authenticated management view */}
                <Route path="/workers" element={<ServiceAutomations />} />
                <Route path="/workers/browse" element={<AgentMarketplaceV2 embedded />} />
                <Route path="/workers/setup" element={<WorkerSetupFlow />} />
                <Route path="/workers/new" element={<AutomationBuilder />} />
                <Route path="/workers/:workerId/detail" element={<WorkerDetail />} />
                <Route path="/workers/memory" element={<MemoryInspector />} />
                <Route path="/workers/mappings" element={<MappingDashboard />} />
                <Route path="/systems" element={<SystemsPage />} />
                <Route path="/systems/:systemId" element={<SystemDetailPage />} />
                <Route path="/workers/activity" element={<SystemRuns />} />
                <Route path="/workers/activity/:runId" element={<SystemRunDetail />} />
                {/* Legacy /automations routes redirect */}
                <Route path="/automations" element={<Navigate to="/workers" replace />} />
                <Route path="/automations/new" element={<Navigate to="/workers/new" replace />} />
                <Route path="/automations/:slug" element={<AutomationDetail />} />
                
                {/* Legacy redirects to unified dashboard */}
                <Route path="/partner-pro/dashboard" element={<Navigate to="/dashboard" replace />} />
                <Route path="/workspace-lite" element={<Navigate to="/dashboard" replace />} />
                
                <Route path="/partner-pro/workspace" element={<WorkspaceHome />} />
                
                {/* Employee Workspace Routes - Phase 11 */}
                <Route path="/partner-pro/workspace/my-agents" element={<MyAgents />} />
                <Route path="/partner-pro/workspace/sessions" element={<SessionsList />} />
                <Route path="/partner-pro/workspace/sessions/:session_id" element={<SessionDetail />} />
                
                {/* Action Console / Inbox - Human-in-the-loop control centre */}
                <Route path="/partner-pro/workspace/inbox" element={<Inbox />} />
                
                {/* V1 Agent Scheduler */}
                <Route path="/partner-pro/workspace/schedules" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <Schedules />
                  </RoleProtectedRoute>
                } />
                
                {/* Agent Dashboard Routes */}
                <Route path="/workspace/dashboards" element={<DashboardsList />} />
                <Route path="/agents/:agentId/dashboard" element={<AgentDashboard />} />
                
                {/* Phase 10: Workflows - Feature flagged for Public Beta */}
                <Route path="/partner-pro/workspace/workflows" element={
                  featureFlags.ENABLE_WORKFLOWS ? <WorkflowsList /> : <WorkflowsComingSoon />
                } />
                <Route path="/partner-pro/workspace/workflows/:workflow_id" element={
                  featureFlags.ENABLE_WORKFLOWS ? <WorkflowDetail /> : <WorkflowsComingSoon />
                } />
                <Route path="/partner-pro/workspace/workflow-runs/:run_id" element={
                  featureFlags.ENABLE_WORKFLOWS ? <WorkflowRunDetail /> : <WorkflowsComingSoon />
                } />
                
                {/* Admin Routes - Protected */}
                <Route path="/admin/integrations" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <IntegrationsSettings />
                  </RoleProtectedRoute>
                } />
                <Route path="/admin/team-members" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <TeamMembers />
                  </RoleProtectedRoute>
                } />
                
                {/* Approval Dashboard — AI OS Control Room */}
                <Route path="/approval-dashboard" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <ApprovalDashboard />
                  </RoleProtectedRoute>
                } />

                {/* External Agent Fabric */}
                <Route path="/external-agents" element={<ExternalAgentLibrary />} />
                <Route path="/my-external-agents" element={<MyExternalAgents />} />
                <Route path="/external-agent-review" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <ExternalAgentReview />
                  </RoleProtectedRoute>
                } />
                <Route path="/external-agent-health" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <ExternalAgentHealth />
                  </RoleProtectedRoute>
                } />
                
                {/* Settings Routes - Admin Only */}
                <Route path="/partner-pro/settings/integrations" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <IntegrationsSettings />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/admin/team-members" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <TeamMembers />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/organisation/ai-configuration" element={
                  <Navigate to="/settings?tab=ai" replace />
                } />
                <Route path="/settings/integrations" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <OAuthIntegrations />
                  </RoleProtectedRoute>
                } />
                {/* Automation Layer V1 Routes */}
                <Route path="/settings/automation-integrations" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <AutomationIntegrations />
                  </RoleProtectedRoute>
                } />
                <Route path="/settings/automations" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <Automations />
                  </RoleProtectedRoute>
                } />
                <Route path="/settings" element={<ServiceSettings />} />
                <Route path="/settings/team" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <TeamMembers />
                  </RoleProtectedRoute>
                } />
                <Route path="/settings/audit" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <AuditLogs />
                  </RoleProtectedRoute>
                } />
                <Route path="/settings/mcp" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <MCPSettings />
                  </RoleProtectedRoute>
                } />
                
                {/* Client Organizations Management (Phase 3.3) */}
                <Route path="/settings/clients" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <ClientOrganizationsPage />
                  </RoleProtectedRoute>
                } />
                
                {/* Legacy Operators Page - Redirect to main agents list */}
                <Route path="/internal/execution-agents" element={
                  <Navigate to="/agents" replace />
                } />
                <Route path="/internal/execution-agents/*" element={
                  <Navigate to="/agents" replace />
                } />
                
                {/* Operator Detail Pages */}
                <Route path="/partner-pro/ai-workforce/operators/:operatorId" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <OperatorDetail />
                  </RoleProtectedRoute>
                } />
                <Route path="/partner-pro/ai-workforce/operators/:operatorId/edit" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <OperatorDetail />
                  </RoleProtectedRoute>
                } />
                
                {/* Unified Analytics - All users can view */}
                <Route path="/analytics" element={<UnifiedAnalytics />} />
                
                {/* Run Health Dashboard */}
                <Route path="/run-health" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <RunHealthDashboard />
                  </RoleProtectedRoute>
                } />
                
                {/* Legacy Analytics Routes - Redirect to unified */}
                <Route path="/organisation/analytics" element={<Navigate to="/analytics?tab=product" replace />} />
                <Route path="/analytics/dashboard" element={<Navigate to="/analytics?tab=agents" replace />} />
                <Route path="/analytics/agents/:agentId" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <AgentAnalytics />
                  </RoleProtectedRoute>
                } />
                <Route path="/analytics/reports" element={
                  <RoleProtectedRoute allowedRoles={['admin', 'primary_admin']}>
                    <AnalyticsReports />
                  </RoleProtectedRoute>
                } />
              </Route>
              
              {/* Redirects */}
              <Route path="/settings/voice" element={<Navigate to="/partner-pro/ai-workforce/voice-settings" replace />} />
              
              {/* Redirects for old Analytics routes */}
              <Route path="/analytics/voice" element={<Navigate to="/analytics?tab=voice" replace />} />
              <Route path="/partner-pro/ai-workforce/analytics" element={<Navigate to="/analytics" replace />} />
              <Route path="/partner-pro/ai-workforce/analytics/agents/:agentId" element={<AgentAnalyticsRedirect />} />

              {/* Tooltip Test Page */}
              <Route path="/tooltip-test-react" element={<TooltipTest />} />

              {/* Admin Routes */}
              {/* Admin Routes - Login and Password Reset (no layout) */}
              {/* Platform Admin Login - separate from regular user login */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
              <Route path="/admin/reset-password" element={<AdminResetPassword />} />
              
              {/* Admin Routes - With Unified Layout */}
              <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="organizations" element={<OrganizationsList />} />
                <Route path="organizations/:orgId" element={<OrganizationDetail />} />
                <Route path="users" element={<UsersList />} />
                <Route path="users/:userId" element={<UserDetail />} />
                <Route path="plans" element={<AdminPlans />} />
                <Route path="billing" element={<BillingFinance />} />
                <Route path="analytics" element={<SiteAnalytics />} />
                <Route path="backups" element={<AdminBackups />} />
                <Route path="logs" element={<LogsAudit />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="blogs" element={<BlogsManagement />} />
                <Route path="blogs/new" element={<BlogEditorNew />} />
                <Route path="blogs/edit/:blogId" element={<BlogEditorNew />} />
                <Route path="ebooks" element={<EbooksManagement />} />
                <Route path="ebooks/new" element={<EbookEditorNew />} />
                <Route path="ebooks/edit/:ebookId" element={<EbookEditorNew />} />
                <Route path="ebook-downloads" element={<EbookDownloadsManagement />} />
                <Route path="messages" element={<MessagesManagement />} />
                <Route path="legal" element={<LegalManagement />} />
                <Route path="settings" element={<PlatformSettings />} />
                <Route path="templates" element={<AdminTemplateManager />} />
                <Route path="submissions" element={<AdminSubmissions />} />
                <Route path="partners" element={<AdminPartners />} />
                <Route path="affiliates" element={<AdminAffiliates />} />
                <Route path="admin-users" element={<AdminUsersManagement />} />
                <Route path="llm-usage" element={<LLMUsageAdmin />} />
                <Route path="llm-keys" element={<AdminLLMKeys />} />
                <Route path="docs" element={<AdminDocumentation />} />
                <Route path="tracking-diagnostics" element={<TrackingDiagnostics />} />
                <Route path="automation-review" element={<AdminReviewQueue />} />
                <Route path="app-review" element={<AdminAppReviewQueue />} />
              </Route>
              
              {/* Service OS — Simplified UI for service businesses */}
              <Route path="/enquiry/:publicKey" element={<PublicEnquiryPage />} />
              <Route path="/service-os/onboarding" element={
                <UserProtectedRoute>
                  <SOSGate onboardingPage><SOSOnboarding /></SOSGate>
                </UserProtectedRoute>
              } />
              <Route path="/service-os" element={
                <UserProtectedRoute>
                  <SOSGate><SOSLayout /></SOSGate>
                </UserProtectedRoute>
              }>
                <Route index element={<SOSDashboard />} />
                <Route path="leads" element={<SOSLeads />} />
                <Route path="leads/:leadId" element={<SOSLeadDetail />} />
                <Route path="jobs" element={<SOSJobs />} />
                <Route path="settings" element={<SOSSettings />} />
              </Route>
              
              {/* 404 Catch-all Route - Must be last */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              </CompletionGuard>
              <CookieConsent />
              
              {/* Public Beta Footer - Global */}
              <PublicBetaFooter />
              
            </BrowserRouter>
          </div>
          </ApprovalWSProvider>
          </CommandProvider>
          </CreditGuardProvider>
          </StripeProvider>
          </OrganizationProvider>
          </ModeProvider>
        </PlanProvider>
        </GlobalAuthGate>
      </AuthProvider>
      </AuthRemountContext.Provider>
    </HelmetProvider>
    </IconContext.Provider>
  );
}

export default App;